import type {
  PublicKeyCredentialCreationOptions as PublicKeyCredentialCreationOptionsJSON,
  RegistrationCredential,
} from '@column/api';

interface PublicKeyCredentialDescriptorJSON {
  id: string;
  transports?: string[];
  type: 'public-key';
}

type PublicKeyCredentialRequestOptionsJSON = Omit<
  PublicKeyCredentialRequestOptions,
  'allowCredentials' | 'challenge'
> & {
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
  challenge: string;
};

export interface AuthenticationCredentialJSON {
  authenticatorAttachment?: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  id: string;
  rawId: string;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle?: string;
  };
  type: 'public-key';
}

export const isWebAuthnSupported = () =>
  typeof window !== 'undefined' && 'PublicKeyCredential' in window && !!navigator.credentials;

export const getDefaultPasskeyName = (firstName: string) => {
  const fallbackName = firstName.trim() ? `${firstName.trim()}'s Passkey` : 'Passkey';

  if (typeof navigator === 'undefined') {
    return fallbackName;
  }

  const userAgent = navigator.userAgent;
  const browser = userAgent.includes('Edg/')
    ? 'Edge'
    : userAgent.includes('Firefox/')
      ? 'Firefox'
      : userAgent.includes('Chrome/') || userAgent.includes('CriOS/')
        ? 'Chrome'
        : userAgent.includes('Safari/')
          ? 'Safari'
          : undefined;
  const device = /iPad/.test(userAgent)
    ? 'iPad'
    : /iPhone/.test(userAgent)
      ? 'iPhone'
      : /Android/.test(userAgent)
        ? 'Android'
        : /CrOS/.test(userAgent)
          ? 'ChromeOS'
          : /Windows/.test(userAgent)
            ? 'Windows'
            : /Macintosh|Mac OS X/.test(userAgent)
              ? 'macOS'
              : /Linux/.test(userAgent)
                ? 'Linux'
                : undefined;

  if (browser && device) {
    return `${browser} on ${device}`;
  }
  return browser ?? device ?? fallbackName;
};

export const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

  return bytes.buffer;
};

export const arrayBufferToBase64Url = (value: ArrayBuffer): string => {
  const binary = Array.from(new Uint8Array(value), (byte) => String.fromCharCode(byte)).join('');

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const credentialDescriptorFromJSON = ({
  id,
  transports,
  ...descriptor
}: PublicKeyCredentialDescriptorJSON): PublicKeyCredentialDescriptor => ({
  ...descriptor,
  id: base64UrlToArrayBuffer(id),
  transports: transports as AuthenticatorTransport[] | undefined,
});

export const createPublicKeyCredential = async (
  options: PublicKeyCredentialCreationOptionsJSON
): Promise<RegistrationCredential> => {
  if (!isWebAuthnSupported()) {
    throw new Error('Passkeys are not supported by this browser.');
  }

  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: base64UrlToArrayBuffer(options.challenge),
      excludeCredentials: options.excludeCredentials?.map(credentialDescriptorFromJSON),
      extensions: options.extensions as AuthenticationExtensionsClientInputs,
      user: {
        ...options.user,
        id: base64UrlToArrayBuffer(options.user.id),
      },
    },
  });

  if (!credential || credential.type !== 'public-key') {
    throw new Error('Passkey creation did not return a credential.');
  }

  const publicKeyCredential = credential as PublicKeyCredential;
  const response = publicKeyCredential.response as AuthenticatorAttestationResponse;
  const publicKey = response.getPublicKey?.();

  return {
    authenticatorAttachment: publicKeyCredential.authenticatorAttachment ?? undefined,
    clientExtensionResults: publicKeyCredential.getClientExtensionResults() as Record<string, unknown>,
    id: publicKeyCredential.id,
    rawId: arrayBufferToBase64Url(publicKeyCredential.rawId),
    response: {
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      authenticatorData: response.getAuthenticatorData
        ? arrayBufferToBase64Url(response.getAuthenticatorData())
        : undefined,
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      publicKey: publicKey ? arrayBufferToBase64Url(publicKey) : undefined,
      publicKeyAlgorithm: response.getPublicKeyAlgorithm?.(),
      transports: response.getTransports?.(),
    },
    type: 'public-key',
  };
};

export const getPublicKeyCredential = async (
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticationCredentialJSON> => {
  if (!isWebAuthnSupported()) {
    throw new Error('Passkeys are not supported by this browser.');
  }

  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      allowCredentials: options.allowCredentials?.map(credentialDescriptorFromJSON),
      challenge: base64UrlToArrayBuffer(options.challenge),
    },
  });

  if (!credential || credential.type !== 'public-key') {
    throw new Error('Passkey authentication did not return a credential.');
  }

  const publicKeyCredential = credential as PublicKeyCredential;
  const response = publicKeyCredential.response as AuthenticatorAssertionResponse;

  return {
    authenticatorAttachment: publicKeyCredential.authenticatorAttachment ?? undefined,
    clientExtensionResults: publicKeyCredential.getClientExtensionResults(),
    id: publicKeyCredential.id,
    rawId: arrayBufferToBase64Url(publicKeyCredential.rawId),
    response: {
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : undefined,
    },
    type: 'public-key',
  };
};
